import * as React from "react";
import {ChangeEvent, useEffect, useRef, useState} from "react";
import {IAutomationConfig} from "../Interfaces/IConfig";
import {
    getAutomationConfig,
    getNightlyResults,
    setAutomationConfig,
    startOrchestration
} from "../BackendFacade/apiCalls";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import {INightlyResults} from "../Interfaces/INightlyResults";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import {odxIconSets, odxVariants} from "../../../OdxComponents/constants";
import OdxInputField from "../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import OdxIcon from "../../../OdxComponents/NativeOdx/Odx-Icon";
import OdxChip from "../../../OdxComponents/NativeOdx/Odx-Chip";
import OdxInlineMessage from "../../../OdxComponents/NativeOdx/Odx-InlineMessage";
import OdxTable from "../../../OdxComponents/NativeOdx/Table/Odx-Table";
import {OdxTableRow} from "../../../OdxComponents/NativeOdx/Table/Odx-TableRow";
import {OdxTableCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Tablecell";
import OdxSelect from "../../../OdxComponents/NativeOdx/FormControls/Odx-Select";
import OdxModal from "../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import OdxDropdown from "../../../OdxComponents/NativeOdx/Odx-Dropdown";
import {NetworkData} from "../../../BackendFacade";
import {useDpasData} from "../dpasDataContext";
import OdxContentBox from "../../../OdxComponents/NativeOdx/Odx-Contentbox";

const Config: React.FC = () => {
    const [confirmOrchestratorModal, setConfirmOrchestratorModal] = useState<boolean>(false);
    const [originalConfig, setOriginalConfig] = useState<IAutomationConfig | null>(null);
    const [nightlyResults, setNightlyResults] = useState<INightlyResults | null>(null);
    const {setLoading, createErrorToast, isLoading, createToast} = useMain();
    const {getProjectId: resolveProjectId, setDpasId} = useDpasData();

    const [automationConfigEmails, setAutomationConfigEmails] = useState<string[]>([]);
    const [automationConfigCurrentEmail, setAutomationConfigCurrentEmail] = useState<string>("");

    const [automationConfigCSV, setAutomationConfigCSV] = useState<string>("");
    const [automationConfigProjectName, setAutomationConfigProjectName] = useState<string>("");

    const [isAddingContact, setIsAddingContact] = useState<boolean>(false);

    const [isAddDropdownOpen, setAddDropdownOpen] = useState<boolean>(false);
    const [isColumnDropdownOpen, setColumnDropdownOpen] = useState<boolean>(false);

    const addButtonRef = useRef<HTMLDivElement>(null);
    const columnButtonRef = useRef<HTMLDivElement>(null);

    const [newRowValues, setNewRowValues] = useState<string[]>([]);
    const [columnAction, setColumnAction] = useState<string>('');
    const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(null);
    const [newColumnName, setNewColumnName] = useState<string>('');
    const [csvToggleEditTable, setCsvToggleEditTable] = useState<'CSV' | 'Table'>('Table');
    const baseId = 'dpas-config';

    useEffect(() => {
        setLoading(true);
        ensureProjectId()
            .then(async (projectId) => {
                if (projectId) {
                    await fetchConfig();
                }
            })
            .finally(() => {
                setLoading(false);
            });
        getNightlyResults().then((r) => {
            setNightlyResults(r.data);
        });
    }, []);

    const isUserroleEditor = () => {
        return isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id, HubPathRouting.currentModule?.id);
    }

// NOTE: If your parent sets <Config key={someValueThat changes with each typed character} />,
// that alone triggers a re-mount every time. Please verify the parent usage.

    const ensureProjectId = async () => {
        const data = await resolveProjectId();
        if (data === "") {
            createErrorToast("Error", "No project found");
            return "";
        }
        setDpasId(data);
        return data;
    };

    const fetchConfig = async () => {
        const data = await getAutomationConfig();
        if (!data.success) {
            createErrorToast("Error", data.message);
            setLoading(false);
            return;
        }
        const automationConfig: IAutomationConfig = data.data;
        if (!automationConfig.config) {
            automationConfig.config = {
                csvFileName: "",
                projectName: "",
                contactMails: []
            };
        }
        if (!automationConfig.csvString) {
            automationConfig.csvString = "SearchName;DisplayName";
        }

        setAutomationConfigEmails(automationConfig.config.contactMails ?? []);
        setAutomationConfigCSV(automationConfig.csvString);
        setOriginalConfig(automationConfig);

        setLoading(false);
    };

    const toggleCsvView = (view: string) => {
        if (view === 'CSV' || view === 'Table') {
            setCsvToggleEditTable(view);
        }
    };

    const automationConfigSubmit = () => {
        if (!originalConfig) return;

        const filename = originalConfig.config.csvFileName
            ? originalConfig.config.csvFileName
            : automationConfigProjectName + ".csv";

        const body: IAutomationConfig = {
            csvString: automationConfigCSV,
            config: {
                csvFileName: filename,
                contactMails: automationConfigEmails,
                projectName: originalConfig.config.projectName
            }
        };
        setLoading(true);
        setAutomationConfig(body).then((resp) => {
            setLoading(false);
            if (!resp.success) {
                createErrorToast("Error", resp.message);
            } else {
                createToast({variant: "success", title: "Success", message: "Configuration saved"});
            }
        });
    };

    const automationConfigAddEmail = () => {
        const cleaned = automationConfigCurrentEmail.trim();
        if (cleaned && !automationConfigEmails.includes(cleaned)) {
            setAutomationConfigEmails([...automationConfigEmails, cleaned]);
            setAutomationConfigCurrentEmail("");
        }
    };

    const automationConfigRemoveEmail = (email: string) => {
        setAutomationConfigEmails(automationConfigEmails.filter((x) => x !== email));
    };

    const uploadConfigCSV = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = ".csv";
        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.readAsText(file, 'UTF-8');
            reader.onload = (readerEvent) => {
                const content = readerEvent.target?.result;
                if (typeof content === 'string') {
                    setAutomationConfigCSV(content.trim());
                }
            };
        };
        input.click();
    };

    const addNewRow = () => {
        const csvLines = automationConfigCSV.split(/\r?\n/);
        const newRow = newRowValues.join(";");
        csvLines.push(newRow);
        setAutomationConfigCSV(csvLines.join("\n"));
        setAddDropdownOpen(false);
        setNewRowValues([]);
    };

    const getCsvDataRowCount = () => {
        // If empty, count is zero
        if (!automationConfigCSV) return 0;
        const lines = automationConfigCSV.split(/\r?\n/).filter(l => l.trim() !== "");
        return Math.max(0, lines.length - 1); // minus header
    };

    const addNewColumn = () => {
        const csvLines = automationConfigCSV.split(/\r?\n/);
        csvLines[0] += `;${newColumnName}`;
        for (let i = 1; i < csvLines.length; i++) {
            csvLines[i] += ";";
        }
        setAutomationConfigCSV(csvLines.join("\n"));
        setNewColumnName('');
        setColumnAction('');
        setColumnDropdownOpen(false);
    };

    const renameColumn = () => {
        const csvLines = automationConfigCSV.split(/\r?\n/);
        let headers = csvLines[0].split(";");
        if (selectedColumnIndex !== null && selectedColumnIndex < headers.length) {
            headers[selectedColumnIndex] = newColumnName;
            csvLines[0] = headers.join(";");
            setAutomationConfigCSV(csvLines.join("\n"));
        }
        setNewColumnName('');
        setSelectedColumnIndex(null);
        setColumnAction('');
        setColumnDropdownOpen(false);
    };

    const deleteColumn = (index: number) => {
        let csvLines = automationConfigCSV.split(/\r?\n/);
        csvLines = csvLines.map((line) => {
            const columns = line.split(";");
            columns.splice(index, 1);
            return columns.join(";");
        });
        setAutomationConfigCSV(csvLines.join("\n"));
        setColumnDropdownOpen(false);
    };

    const deleteRow = (index: number) => {
        const csvLines = automationConfigCSV.split(/\r?\n/);
        csvLines.splice(index + 1, 1); // row 0 is header
        setAutomationConfigCSV(csvLines.join("\n"));
    };

    const NightlyResultsContent = () => {
        const [isModalOpen, setIsModalOpen] = useState(false);
        const sectionId = `${baseId}-nightly`;
        if (!nightlyResults) return null;

        return (
            <>
                <OdxContentBox title="Nightly Update Results" className="!p-0" id={`${sectionId}-summary`}>
                    <OdxInlineMessage
                        variant={nightlyResults.message.isSuccess ? "success" : "danger"}
                        text={
                            nightlyResults.message.isSuccess
                                ? "Nightly update completed successfully"
                                : "Errors occurred during nightly update. Check details."
                        }
                    />
                    <div className="flex justify-between items-center">
                    <span>
                        {nightlyResults.time && new Date(nightlyResults.time * 1000).toLocaleDateString('de-DE')}{" "}
                        {nightlyResults.time &&
                            new Date(nightlyResults.time * 1000).toLocaleTimeString('de-DE', {
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        <span className="!ml-4">{nightlyResults.message.messageString}</span>
                    </span>
                        <odx-button
                            variant="accent"
                            type="button"
                            className="ml-auto"
                            onClick={() => setIsModalOpen(true)}
                            id={`${sectionId}-open-modal-btn`}
                        >
                            Show JSON Response
                        </odx-button>
                    </div>
                </OdxContentBox>

                {isModalOpen && (
                    <OdxModal
                        variant="sidesheet"
                        size="medium"
                        title="JSON Response"
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        footerButtons={[
                            <odx-button
                                key="close"
                                variant="primary"
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                id={`${sectionId}-close-modal-btn`}
                            >
                                Close
                            </odx-button>
                        ]}
                        id={`${sectionId}-modal`}
                    >
                        <JsonDisplay json={nightlyResults}/>
                    </OdxModal>
                )}
            </>
        );
    };

    const JsonDisplay = ({ json }: { json: INightlyResults }) => {
        const { isSuccess, messageString, errorObjectList } = json.message;
        return (
            <div className="json-display">
                <h2>{isSuccess ? "Success" : "Error"}</h2>
                <p>{messageString}</p>
                {errorObjectList && errorObjectList.length > 0 && (
                    <div>
                        <h3>Error Details:</h3>
                        <ul>
                            {errorObjectList.map((error, idx) => (
                                <li key={idx}>
                                    <strong>Search Key:</strong> {error.searchKey}
                                    <br />
                                    <strong>Message:</strong> {error.message}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const EmailContent = () => {
        const sectionId = `${baseId}-contacts`;
        return (
                <OdxContentBox
                    title="Contacts"
                    className=""
                    id={sectionId}
                    action={
                        <div className="flex justify-end items-start space-x-4">
                            {!isAddingContact && (
                                <odx-button
                                    variant="accent"
                                    type="button"
                                    id={`${sectionId}-add-btn`}
                                    onClick={() => setIsAddingContact(true)}
                                    disabled={!isUserroleEditor() ? true : undefined}
                                >
                                    <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                    Add Email
                                </odx-button>
                            )}
                        </div>
                    }
                >
                    {isAddingContact && (
                        <div className="flex flex-col items-center mt-4 p-5" id={`${sectionId}-form`}>
                            <OdxInputField
                                label="Email Address"
                                id={`${sectionId}-email-input`}
                                isRequired={true}
                                value={automationConfigCurrentEmail}
                                // Debug on change
                                onChange={(event) => {
                                    setAutomationConfigCurrentEmail(event.target.value);
                                }}
                                className="flex-1 w-1/2"
                            />
                            <div>
                                {isUserroleEditor() && (
                                    <odx-button
                                        variant="primary"
                                        type="button"
                                        id={`${sectionId}-submit-btn`}
                                        onClick={() => {
                                            automationConfigAddEmail();
                                            setIsAddingContact(false);
                                        }}
                                    >
                                        Submit
                                    </odx-button>)}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap" id={`${sectionId}-list`}>
                        {automationConfigEmails.map((email, index) => (
                            <div key={email} className={"flex !mr-2"}>
                            <OdxChip
                                removable={isUserroleEditor()}
                                size="medium"
                                id={`${sectionId}-chip-${index}`}
                                variant={odxVariants.secondary}
                                content={email}
                                onRemove={() => automationConfigRemoveEmail(email)}
                                truncateText
                            />
                            </div>
                        ))}
                    </div>
                </OdxContentBox>
        );
    };

    const CsvContent = () => {
        return (
            <OdxContentBox title="CSV Content" id={`${baseId}-csv`}>
            <textarea
                className="bg-gray-100 border border-gray-300 rounded-md p-2 shadow-inner resize-y w-full"
                value={automationConfigCSV}
                onChange={(e) => setAutomationConfigCSV(e.target.value)}
                rows={10}
            />
            </OdxContentBox>
        );
    };

    const AutomationConfigDetailsContent = () => {
        const lines = automationConfigCSV.split(/\r?\n/);
        if (!lines[0]) lines[0] = "SearchName;DisplayName";
        const headers = lines[0].split(";");

        const options = [
            { value: 'CSV', label: 'CSV View' },
            { value: 'Table', label: 'Table View' },
        ];

        const handleCellChange = (value: string, rowIndex: number, colIndex: number) => {
            const updatedLines = [...lines];
            const updatedRow = updatedLines[rowIndex].split(";");
            updatedRow[colIndex] = value;
            updatedLines[rowIndex] = updatedRow.join(";");
            setAutomationConfigCSV(updatedLines.join("\n"));
        };

        const csvRowCount = getCsvDataRowCount();
        const csvTitle = `CSV Content (${csvRowCount} row${csvRowCount !== 1 ? "s" : ""})`;
        const sectionId = `${baseId}-csv-details`;

        return (
            <OdxContentBox
                title={csvTitle}
                id={sectionId}
                action={
                    <div className="flex justify-end items-start space-x-4">
                        <div style={{ marginTop: '-14px' }} className={"!mr-5"}>
                            <OdxSelect
                                options={options}
                                value={csvToggleEditTable}
                                id={`${sectionId}-view-toggle`}
                                onChange={(event: ChangeEvent<HTMLSelectElement>) => toggleCsvView(event.target.value)}
                                placeholder="Select view"
                            />
                        </div>
                        <odx-button
                            variant="accent"
                            type="button"
                            onClick={uploadConfigCSV}
                            id={`${sectionId}-import-btn`}
                        >
                            Import from file
                        </odx-button>
                    </div>
                }
            >
                {csvToggleEditTable === 'Table' && (
                    <div>
                        <div className="flex justify-start mt-4">
                            <div ref={addButtonRef}>
                                <odx-button
                                    id={`${sectionId}-add-row-btn`}
                                    variant="ghost"
                                    size="lg"
                                    type="button"
                                    onClick={() => setAddDropdownOpen(!isAddDropdownOpen)}
                                >
                                    <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                    Add Row
                                </odx-button>
                            </div>
                        </div>
                        <OdxTable id={`${sectionId}-table`}>
                            {lines.map((line, rowIndex) => {
                                const cells = line.split(";");
                                if(cells.length == 1) {
                                    cells.push(""); // Ensure at least two cells for the header row
                                }
                                return (
                                    <OdxTableRow key={`row-${rowIndex}`}>
                                        {cells.map((subitem, colIndex) => (
                                            <OdxTableCell key={`cell-${rowIndex}-${colIndex}`}>
                                            {rowIndex === 0 ? (
                                                    subitem
                                                ) : (
                                                        <OdxInputField
                                                            value={subitem}
                                                            onChange={(event) =>
                                                                handleCellChange(event.target.value, rowIndex, colIndex)
                                                            }
                                                            className="w-full"
                                                            id={`${sectionId}-cell-${rowIndex}-${colIndex}`}
                                                        />
                                                )}
                                            </OdxTableCell>
                                        ))}
                                        <OdxTableCell>
                                            {rowIndex !== 0 && (
                                                <odx-icon-button
                                                    id={`${sectionId}-delete-row-${rowIndex}`}
                                                    variant="ghost"
                                                    size="lg"
                                                    icon={`${odxIconSets.core}::delete`}
                                                    onClick={() => deleteRow(rowIndex - 1)}
                                                />
                                                )}
                                        </OdxTableCell>
                                    </OdxTableRow>
                                );
                            })}
                        </OdxTable>
                    </div>
                )}

                {csvToggleEditTable === 'CSV' && CsvContent()}

                {isAddDropdownOpen && (
                    <OdxDropdown
                        content={
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-4">Add New Row</h3>
                                {headers.map((header, idx) => (
                                    <OdxInputField
                                        id={`newRow-${idx}`}
                                        label={header}
                                        isRequired
                                        value={newRowValues[idx] || ''}
                                        onChange={(event) => {
                                            const updated = [...newRowValues];
                                            updated[idx] = event.target.value;
                                            setNewRowValues(updated);
                                        }}
                                        className="mb-2"
                                    />
                                ))}
                                <div className="flex justify-end mt-4">
                                    <odx-button
                                        variant="ghost"
                                        size="lg"
                                        type="button"
                                        onClick={() => {
                                            setAddDropdownOpen(false);
                                            setNewRowValues([]);
                                        }}
                                        className="mr-2"
                                        id={`${sectionId}-cancel-new-row-btn`}
                                    >
                                        Cancel
                                    </odx-button>
                                    <odx-button
                                        variant="primary"
                                        size="lg"
                                        type="button"
                                        onClick={addNewRow}
                                        disabled={newRowValues.length === 0 ? true : undefined}
                                        id={`${sectionId}-confirm-new-row-btn`}
                                    >
                                        Add
                                    </odx-button>
                                </div>
                            </div>
                        }
                        position="bottom-start"
                        showArrow
                        matchReferenceWidth={false}
                        offset={0}
                        isOpen={isAddDropdownOpen}
                        setIsOpen={setAddDropdownOpen}
                        buttonRef={addButtonRef}
                    />
                )}
            </OdxContentBox>
        );
    };



    return (
        <div id={`${baseId}-view`}>
            {confirmOrchestratorModal && (
                <OdxModal
                    variant="default"
                    size="small"
                    title="Confirm Orchestrator"
                    isOpen={confirmOrchestratorModal}
                    onClose={() => setConfirmOrchestratorModal(false)}
                    footerButtons={[
                        <odx-button
                            key="close"
                            id={`${baseId}-orchestrator-cancel-btn`}
                            variant="neutral"
                            size="lg"
                            type="button"
                            onClick={() => setConfirmOrchestratorModal(false)}
                        >
                            Close
                        </odx-button>,
                        <odx-button
                            key="confirm"
                            id={`${baseId}-orchestrator-confirm-btn`}
                            variant="primary"
                            size="lg"
                            type="button"
                            onClick={() => {
                                setConfirmOrchestratorModal(false);
                                startOrchestration().then((resp) => {
                                    createToast({
                                        variant: "success",
                                        title: "Started DPAS Orchestrator",
                                        message: "The DPAS Orchestrator has been started.",
                                    });

                                })
                            }}
                        >
                            Confirm
                        </odx-button>
                    ]}
                    id={`${baseId}-orchestrator-modal`}
                >
                    <div>
                        <p>Are you sure you want to start the DPAS Orchestrator?</p>
                        <p>This may take a couple of hours.</p>
                    </div>
                </OdxModal>
            )}
            <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>
            <OdxAreaHeader text="Configuration"
                           id={`${baseId}-header`}
                           odxContent={isUserroleEditor() &&
                               <odx-button
                                   variant="accent"
                                   size="lg"
                                   type="button"
                                   onClick={() => setConfirmOrchestratorModal(true)}
                                   id={`${baseId}-start-orchestrator-btn`}
                               >
                                   <odx-icon slot="prefix" name={`${odxIconSets.safety}::in-process`} size="md"></odx-icon>
                                   Start DPAS Orchestrator
                               </odx-button>
                           }
            />
            <div className="grid grid-cols-1 md:grid-cols-2" id={`${baseId}-grid`}>
                {NightlyResultsContent()}
                {EmailContent()}
            </div>
            <div className="pt-11" id={`${baseId}-details`}>
                {AutomationConfigDetailsContent()}
            </div>
            <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 z-10 flex justify-end mb-4">
                <odx-icon-button
                    id={`${baseId}-save-btn`}
                    variant={automationConfigEmails.length > 0 ? "accent" : "warning"}
                    size="lg"
                    type="button"
                    icon={`${odxIconSets.core}::save`}
                    onClick={automationConfigSubmit}
                    disabled={automationConfigEmails.length === 0 ? true : undefined}
                />
            </div>
        </div>
    );
};

export default Config;
